import moment from "moment";

/**
 * Formats a given date/time string, timestamp, or Date object into a relative time string
 * (e.g. "5 minutes ago", "2 hours ago", "3 days ago").
 *
 * Uses UTC parsing so naive server datetimes without timezone offsets are correctly
 * compared against current UTC time, avoiding client-server timezone mismatch issues.
 */
export const formatTimeAgo = (
  date: string | number | Date | null | undefined,
): string => {
  if (!date) return "";

  let m: moment.Moment;

  if (typeof date === "string") {
    // Attempt strict ISO-8601 parsing first to prevent deprecation warnings on invalid inputs
    m = moment.utc(date, moment.ISO_8601, true);
    if (!m.isValid()) {
      const timestamp = Date.parse(date);
      if (Number.isNaN(timestamp)) {
        return "";
      }
      m = moment.utc(timestamp);
    }
  } else {
    m = moment.utc(date);
  }

  if (!m.isValid()) return "";

  const now = moment.utc();
  const diffMs = now.diff(m);

  // If date is slightly in the future due to slight client/server clock skew (up to 1 minute),
  // display as "a few seconds ago" rather than "in a few seconds".
  if (diffMs < 0 && diffMs > -60000) {
    return m.from(m.clone().add(1, "second"));
  }

  return m.fromNow();
};
